/**
 * Spatially cut freeform (horizontal-assembly) foundation sections with
 * door/window openings — the reverse of computeWallFoundationIntersections.
 *
 * For each opening with WallOpening geometry, we test plan ∩ and Z overlap
 * against linear foundation section profiles. Matching openings become
 * FoundationSectionOpeningDescriptor cuts and are applied via
 * makeLinearFoundationSection (wall gaps + gapAbove/gapBelow).
 */
import { wallOpeningOpening3, wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type {
  FoundationSection,
  FoundationSectionOpeningDescriptor,
  FoundationStrip,
  FoundationSubSystem,
  HalfEdgeToFoundationStrip,
  LinearFoundationSection,
  LinearFoundationSectionSize,
} from "building-types/foundations/foundationTypes";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { offsetPolyLine2 } from "geom/offset";
import { polygonIntersection } from "geom/polygon";
import { polyLine2Copy } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3AABB3 } from "geom/polyLine3";
import { getDoorRoughOpeningAdjustment, isGarageDoorOperationType } from "../doors/doorUtils";
import {
  buildFoundationSectionExtrusions,
  isLinearFoundationSection,
  makeLinearFoundationSection,
} from "./foundationStrip";

// Match Studio's default typed precision for feet/inches fields (1/64").
const STUDIO_TYPED_LENGTH_PRECISION_INCHES = 1 / 64;

const WINDOW_ROUGH_OPENING_ADJUSTMENT = 0.1;

export interface ComputeFoundationOpeningCutsParams {
  readonly doorPlacements: Table<GeneratedDoorPlacement>;
  readonly windowPlacements: Table<GeneratedWindowPlacement>;
  readonly doorSettings: DoorSettings;
}

/**
 * Builds opening cut descriptors from door/window placements that have WallOpening geometry.
 * Outline/top/depth match foundationPathOpenings so makeLinearFoundationSection can reuse
 * the same wall-gap path used by auto-generated foundations.
 */
export function openingDescriptorsFromPlacements({
  doorPlacements,
  windowPlacements,
  doorSettings,
}: ComputeFoundationOpeningCutsParams): FoundationSectionOpeningDescriptor[] {
  const openings: FoundationSectionOpeningDescriptor[] = [];

  for (const doorId in doorPlacements) {
    const door = doorPlacements[doorId];
    if (!door?.opening) {
      continue;
    }
    const { opening } = door;
    const planOutline = wallOpeningPlanExtents(opening);
    const frontOutline3 = wallOpeningOpening3(opening, "FRONT", false);
    if (!frontOutline3) {
      continue;
    }
    const roughOpeningAdjustment = getDoorRoughOpeningAdjustment(door, doorSettings);
    const outline = offsetPolyLine2(polyLine2Copy(planOutline), roughOpeningAdjustment);
    const aabb3 = polyLine3AABB3(ensureDefined(frontOutline3));
    let depth = Math.abs(aabb3.maxZ - aabb3.minZ);
    const top = aabb3.maxZ;
    if (isGarageDoorOperationType(door)) {
      depth = Number.MAX_VALUE;
    }
    openings.push({ outline, top, depth });
  }

  for (const windowId in windowPlacements) {
    const window = windowPlacements[windowId];
    if (!window?.opening) {
      continue;
    }
    const { opening } = window;
    const planOutline = wallOpeningPlanExtents(opening);
    const frontOutline3 = wallOpeningOpening3(opening, "FRONT", false);
    if (!frontOutline3) {
      continue;
    }
    const gapOffset = WINDOW_ROUGH_OPENING_ADJUSTMENT;
    const outline = offsetPolyLine2(polyLine2Copy(planOutline), gapOffset);
    const aabb3 = polyLine3AABB3(ensureDefined(frontOutline3));
    openings.push({
      outline,
      top: aabb3.maxZ + gapOffset,
      depth: Math.abs(aabb3.maxZ - aabb3.minZ) + gapOffset * 2,
    });
  }

  return openings;
}

function openingOverlapsSectionZ(
  opening: FoundationSectionOpeningDescriptor,
  sectionTop: number,
  sectionBottom: number,
) {
  const openingBottom = opening.top - opening.depth;
  const belowSectionBy = sectionBottom - opening.top;
  const aboveSectionBy = openingBottom - sectionTop;
  // Only cut if there's greater than 1/64" vertical overlap.
  return (
    belowSectionBy <= -STUDIO_TYPED_LENGTH_PRECISION_INCHES && aboveSectionBy <= -STUDIO_TYPED_LENGTH_PRECISION_INCHES
  );
}

function openingOverlapsSectionPlan(openingOutline: PolyLine2, planProfiles: PolyLine2[]): boolean {
  for (const profile of planProfiles) {
    const intersection = polygonIntersection([[openingOutline], [profile]]);
    if (intersection.length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Returns opening descriptors that spatially intersect a linear section in plan and Z.
 * This is the freeform-safe association: no half-edge / planar-graph identity required.
 */
export function filterOpeningsOverlappingSection(
  openings: FoundationSectionOpeningDescriptor[],
  section: LinearFoundationSection,
): FoundationSectionOpeningDescriptor[] {
  const sectionTop = section.size.top;
  const sectionBottom = section.size.top - section.size.depth;
  return openings.filter((opening) => {
    if (!openingOverlapsSectionZ(opening, sectionTop, sectionBottom)) {
      return false;
    }
    return openingOverlapsSectionPlan(opening.outline, section.planProfiles);
  });
}

function applyOpeningsToLinearSection(
  section: LinearFoundationSection,
  openings: FoundationSectionOpeningDescriptor[],
): FoundationSection {
  if (openings.length === 0) {
    return section;
  }

  const size: LinearFoundationSectionSize = {
    ...section.size,
    openings,
  };

  const intermediate = makeLinearFoundationSection({
    foundationPath: section.path,
    startCap: section.startCap,
    endCap: section.endCap,
    size,
  });

  return buildFoundationSectionExtrusions(intermediate);
}

function applyOpeningsToStrip(strip: FoundationStrip, openings: FoundationSectionOpeningDescriptor[]): FoundationStrip {
  let changed = false;
  const sections = strip.sections.map((section) => {
    if (!isLinearFoundationSection(section)) {
      return section;
    }
    const overlapping = filterOpeningsOverlappingSection(openings, section);
    if (overlapping.length === 0) {
      return section;
    }
    changed = true;
    return applyOpeningsToLinearSection(section, overlapping);
  });

  if (!changed) {
    return strip;
  }

  return {
    ...strip,
    sections,
  };
}

/**
 * Applies spatially matched opening cuts to linear sections of a foundation subsystem.
 * Isolated and non-overlapping sections are left unchanged.
 */
export function applyOpeningCutsToFoundationSubSystem(
  subSystem: FoundationSubSystem,
  openings: FoundationSectionOpeningDescriptor[],
): FoundationSubSystem {
  if (openings.length === 0) {
    return subSystem;
  }

  let changed = false;
  const strips: HalfEdgeToFoundationStrip = {};
  for (const halfEdgeKey in subSystem.strips) {
    const halfEdgeIndex = Number(halfEdgeKey);
    const strip = subSystem.strips[halfEdgeIndex];
    const updated = applyOpeningsToStrip(strip, openings);
    strips[halfEdgeIndex] = updated;
    if (updated !== strip) {
      changed = true;
    }
  }

  if (!changed) {
    return subSystem;
  }

  return {
    ...subSystem,
    strips,
  };
}

/**
 * Collects opening descriptors from placements and applies them to every foundation
 * subsystem that has spatially overlapping linear sections.
 */
export function applyOpeningCutsToFoundationHorizontalAssemblies<
  T extends { readonly subSystem: FoundationSubSystem },
>({
  foundations,
  doorPlacements,
  windowPlacements,
  doorSettings,
}: {
  readonly foundations: Table<T>;
  readonly doorPlacements: Table<GeneratedDoorPlacement>;
  readonly windowPlacements: Table<GeneratedWindowPlacement>;
  readonly doorSettings: DoorSettings;
}): Table<T> {
  const openings = openingDescriptorsFromPlacements({
    doorPlacements,
    windowPlacements,
    doorSettings,
  });

  if (openings.length === 0 || Object.keys(foundations).length === 0) {
    return foundations;
  }

  const result: { [id: string]: T } = {};
  let changed = false;

  for (const foundationId in foundations) {
    const foundation = foundations[foundationId];
    const updatedSubSystem = applyOpeningCutsToFoundationSubSystem(foundation.subSystem, openings);
    if (updatedSubSystem === foundation.subSystem) {
      result[foundationId] = foundation;
      continue;
    }
    changed = true;
    result[foundationId] = {
      ...foundation,
      subSystem: updatedSubSystem,
    };
  }

  return changed ? result : foundations;
}
